import { useCallback, useState } from "react";
import { FileUp, Upload } from "lucide-react";
import { upsertDailySales, insertImportLog, fetchPayPeriods } from "../tipsApi";

interface ParsedRow {
  date: string;
  tips: number;
  sales: number;
  status: "ready" | "skipped";
  reason?: string;
}

function parseCSV(text: string): string[][] {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  return lines.map((line) => {
    const cells: string[] = [];
    let cur = "";
    let inQuotes = false;
    for (const ch of line) {
      if (ch === '"') {
        inQuotes = !inQuotes;
      } else if (ch === "," && !inQuotes) {
        cells.push(cur.trim());
        cur = "";
      } else {
        cur += ch;
      }
    }
    cells.push(cur.trim());
    return cells;
  });
}

function tryParseDate(val: string): string | null {
  const cleaned = val.replace(/['"]/g, "").trim();
  const d = new Date(cleaned);
  if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  const parts = cleaned.match(/^(\d{1,2})[/\-](\d{1,2})[/\-](\d{2,4})$/);
  if (parts) {
    const yr = parts[3].length === 2 ? "20" + parts[3] : parts[3];
    const m = parts[1].padStart(2, "0");
    const day = parts[2].padStart(2, "0");
    return `${yr}-${m}-${day}`;
  }
  return null;
}

function parseMoney(val: string): number {
  return parseFloat(val.replace(/[$,]/g, "")) || 0;
}

export function PosImport() {
  const [fileName, setFileName] = useState<string | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [dateCol, setDateCol] = useState(0);
  const [tipsCol, setTipsCol] = useState(1);
  const [salesCol, setSalesCol] = useState(2);
  const [parsed, setParsed] = useState<ParsedRow[]>([]);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<{ imported: number; skipped: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setResult(null);
    setError(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const data = parseCSV(text);
      if (data.length < 2) {
        setError("CSV must have a header row and at least one data row.");
        return;
      }
      setHeaders(data[0]);
      setRows(data.slice(1));
      const hLower = data[0].map((h) => h.toLowerCase());
      setDateCol(Math.max(0, hLower.findIndex((h) => h.includes("date"))));
      setTipsCol(Math.max(0, hLower.findIndex((h) => h.includes("tip"))));
      setSalesCol(Math.max(0, hLower.findIndex((h) => h.includes("sale") || h.includes("revenue") || h.includes("total"))));
      setParsed([]);
    };
    reader.readAsText(file);
  }

  const preview = useCallback(async () => {
    setError(null);
    try {
      const periods = await fetchPayPeriods();
      const lockedDates = new Set<string>();
      const periodDates = new Set<string>();
      for (const pp of periods) {
        let d = pp.start_date;
        while (d <= pp.end_date) {
          periodDates.add(d);
          if (pp.is_locked) lockedDates.add(d);
          const next = new Date(d + "T00:00:00");
          next.setDate(next.getDate() + 1);
          d = next.toISOString().slice(0, 10);
        }
      }
      const result: ParsedRow[] = rows.map((row) => {
        const dateStr = tryParseDate(row[dateCol] ?? "");
        if (!dateStr) return { date: row[dateCol] ?? "", tips: 0, sales: 0, status: "skipped" as const, reason: "Invalid date" };
        if (lockedDates.has(dateStr)) return { date: dateStr, tips: parseMoney(row[tipsCol] ?? "0"), sales: parseMoney(row[salesCol] ?? "0"), status: "skipped" as const, reason: "Period locked" };
        return {
          date: dateStr,
          tips: parseMoney(row[tipsCol] ?? "0"),
          sales: parseMoney(row[salesCol] ?? "0"),
          status: "ready" as const,
        };
      });
      setParsed(result);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [rows, dateCol, tipsCol, salesCol]);

  async function handleImport() {
    setImporting(true);
    setError(null);
    try {
      const ready = parsed.filter((r) => r.status === "ready" && (r.tips > 0 || r.sales > 0));
      for (const row of ready) {
        if (row.sales > 0) {
          await upsertDailySales(row.date, row.sales);
        }
      }
      const imported = ready.length;
      const skipped = parsed.length - imported;
      await insertImportLog({
        filename: fileName ?? "unknown.csv",
        import_type: "pos_csv",
        row_count: parsed.length,
        imported_count: imported,
        skipped_count: skipped,
        imported_by: null,
      });
      setResult({ imported, skipped });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setImporting(false);
    }
  }

  return (
    <div>
      <h3 className="mb-3 text-sm font-semibold">POS CSV Import</h3>

      {/* File picker */}
      <label className="mb-4 flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-tribal-border px-4 py-3 text-sm text-tribal-muted transition-colors hover:border-tribal-red/50 hover:text-white">
        <FileUp className="h-5 w-5" />
        {fileName ?? "Choose a CSV file…"}
        <input type="file" accept=".csv" onChange={handleFile} className="hidden" />
      </label>

      {error && (
        <div className="mb-4 rounded-lg bg-red-900/20 px-3 py-2 text-sm text-red-400">{error}</div>
      )}

      {result && (
        <div className="mb-4 rounded-lg bg-emerald-900/20 px-3 py-2 text-sm text-emerald-400">
          Import complete: {result.imported} rows imported, {result.skipped} skipped.
        </div>
      )}

      {/* Column mapping */}
      {headers.length > 0 && (
        <div className="mb-4 space-y-2">
          <p className="text-xs text-tribal-muted">Map CSV columns:</p>
          <div className="flex flex-wrap gap-3 text-xs">
            <div>
              <label className="mb-0.5 block text-tribal-muted">Date Column</label>
              <select value={dateCol} onChange={(e) => setDateCol(+e.target.value)} className="rounded border border-tribal-border bg-tribal-bg px-2 py-1 text-sm">
                {headers.map((h, i) => <option key={i} value={i}>{h}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-0.5 block text-tribal-muted">Tips Column</label>
              <select value={tipsCol} onChange={(e) => setTipsCol(+e.target.value)} className="rounded border border-tribal-border bg-tribal-bg px-2 py-1 text-sm">
                {headers.map((h, i) => <option key={i} value={i}>{h}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-0.5 block text-tribal-muted">Sales Column</label>
              <select value={salesCol} onChange={(e) => setSalesCol(+e.target.value)} className="rounded border border-tribal-border bg-tribal-bg px-2 py-1 text-sm">
                {headers.map((h, i) => <option key={i} value={i}>{h}</option>)}
              </select>
            </div>
          </div>
          <button
            type="button"
            onClick={preview}
            className="rounded-md bg-white/10 px-3 py-1.5 text-xs font-medium text-white hover:bg-white/15"
          >
            Preview
          </button>
        </div>
      )}

      {/* Preview table */}
      {parsed.length > 0 && (
        <div className="mb-4 max-h-72 overflow-auto rounded-lg border border-tribal-border">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-tribal-surface">
              <tr className="border-b border-tribal-border text-left text-tribal-muted">
                <th className="px-3 py-1.5 font-medium">Date</th>
                <th className="px-3 py-1.5 text-right font-medium">Tips</th>
                <th className="px-3 py-1.5 text-right font-medium">Sales</th>
                <th className="px-3 py-1.5 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {parsed.map((r, i) => (
                <tr key={i} className={`border-b border-tribal-border last:border-0 ${r.status === "skipped" ? "opacity-50" : ""}`}>
                  <td className="px-3 py-1">{r.date}</td>
                  <td className="px-3 py-1 text-right tabular-nums">${r.tips.toFixed(2)}</td>
                  <td className="px-3 py-1 text-right tabular-nums">${r.sales.toFixed(2)}</td>
                  <td className="px-3 py-1">
                    {r.status === "ready" ? (
                      <span className="text-emerald-400">Ready</span>
                    ) : (
                      <span className="text-amber-300">{r.reason}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {parsed.length > 0 && parsed.some((r) => r.status === "ready") && !result && (
        <button
          type="button"
          onClick={handleImport}
          disabled={importing}
          className="flex items-center gap-1.5 rounded-md bg-tribal-red px-4 py-1.5 text-sm font-medium text-white transition-colors hover:bg-tribal-red/80 disabled:opacity-50"
        >
          <Upload className="h-4 w-4" /> {importing ? "Importing…" : "Import"}
        </button>
      )}
    </div>
  );
}
